import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { makeGetColumnWidth, setWidth } from '../../resizing/redux/widthsSlice';
import { useWidthsDispatch, useWidthsSelector } from '../../redux/reduxHooks';
import { ContentComponent } from './getContentComponent';
import { getColumnKey } from '../../table/utils/getColumnKey';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { shouldBypassContextMenu } from '../../contextMenu/shouldBypassContextMenu';
import { SortTH } from 'foundations-components/transitional/components/Table';
import { jsx as _jsx } from "react/jsx-runtime";
const getSortFromDirection = sortDirection => {
  if (sortDirection === -1) {
    return 'ascending';
  }
  if (sortDirection === 1) {
    return 'descending';
  }
  return 'none';
};
const getDirectionFromSort = sort => sort === 'ascending' ? -1 : 1;
const ContextMenuMinWidthContainer = styled.div.withConfig({
  displayName: "ResizableSortTH__ContextMenuMinWidthContainer"
})(["min-width:", "px;overflow:hidden;"], ({
  $minWidth
}) => $minWidth);
const StopPropogationContainer = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  const handleClick = _temp;
  let t1;
  if ($[0] !== children) {
    t1 = /*#__PURE__*/_jsx("div", {
      onClick: handleClick,
      onContextMenu: handleClick,
      children: children
    });
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ResizableSortTH = t0 => {
  const $ = _c(71);
  const {
    column,
    sortDirection,
    columnIndex,
    onColumnWidthChange,
    onColumnOrderChange,
    onSortChange,
    children
  } = t0;
  const {
    columnName,
    columnNamespace,
    sortable,
    width: columnWidth,
    minimumColumnWidth
  } = column;
  let t1;
  if ($[0] !== columnName || $[1] !== columnNamespace) {
    t1 = getColumnKey(columnNamespace, columnName);
    $[0] = columnName;
    $[1] = columnNamespace;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const columnKey = t1;
  let t2;
  if ($[3] !== columnKey) {
    t2 = makeGetColumnWidth(columnKey);
    $[3] = columnKey;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getColumnWidth = t2;
  const width = useWidthsSelector(getColumnWidth);
  const widthRef = useRef(width);
  let t3;
  let t4;
  if ($[5] !== width) {
    t3 = () => {
      widthRef.current = width;
    };
    t4 = [width];
    $[5] = width;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== columnName || $[9] !== columnNamespace || $[10] !== onColumnWidthChange) {
    t5 = () => {
      onColumnWidthChange({
        columnNamespace,
        columnName,
        nextWidth: widthRef.current
      });
    };
    $[8] = columnName;
    $[9] = columnNamespace;
    $[10] = onColumnWidthChange;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onResizeEnd = t5;
  const dispatch = useWidthsDispatch();
  let t6;
  if ($[12] !== columnKey || $[13] !== dispatch || $[14] !== minimumColumnWidth) {
    t6 = nextWidth => dispatch(setWidth({
      columnKey,
      width: Math.max(nextWidth, minimumColumnWidth)
    }));
    $[12] = columnKey;
    $[13] = dispatch;
    $[14] = minimumColumnWidth;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleResize = t6;
  let t7;
  if ($[16] !== columnName || $[17] !== columnNamespace || $[18] !== onSortChange) {
    t7 = t8 => {
      const {
        target: t9
      } = t8;
      const {
        value
      } = t9;
      return onSortChange({
        columnNamespace,
        columnName,
        direction: getDirectionFromSort(value)
      });
    };
    $[16] = columnName;
    $[17] = columnNamespace;
    $[18] = onSortChange;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleSortChange = t7;
  const contextMenuConfig = useFDTContextMenuConfig();
  const triggerContextMenu = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.triggerContextMenu;
  const ContextMenuContentComponent = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.ContextMenuContentComponent;
  let t8;
  if ($[20] !== columnName || $[21] !== columnNamespace || $[22] !== contextMenuConfig) {
    t8 = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.getIsContextMenuOpen({
      row: null,
      columnName,
      columnNamespace
    });
    $[20] = columnName;
    $[21] = columnNamespace;
    $[22] = contextMenuConfig;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const isContextMenuOpen = t8;
  let t9;
  if ($[24] !== triggerContextMenu) {
    t9 = () => {
      if (triggerContextMenu) {
        triggerContextMenu({
          row: null,
          columnName: null,
          columnNamespace: null
        });
      }
    };
    $[24] = triggerContextMenu;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const onCloseContextMenu = t9;
  let t10;
  if ($[26] !== column || $[27] !== columnIndex || $[28] !== columnWidth || $[29] !== handleResize || $[30] !== onColumnOrderChange || $[31] !== onResizeEnd || $[32] !== sortDirection) {
    t10 = t11 => {
      const {
        arrowsNode,
        children: contentChildren
      } = t11;
      return /*#__PURE__*/_jsx(ContentComponent, {
        arrowsNode: arrowsNode,
        column: column,
        originalWidth: columnWidth,
        columnIndex: columnIndex,
        handleResize: handleResize,
        onResizeEnd: onResizeEnd,
        onColumnOrderChange: onColumnOrderChange,
        sortDirection: sortDirection,
        children: contentChildren
      });
    };
    $[26] = column;
    $[27] = columnIndex;
    $[28] = columnWidth;
    $[29] = handleResize;
    $[30] = onColumnOrderChange;
    $[31] = onResizeEnd;
    $[32] = sortDirection;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const Content = t10;
  let t11;
  if ($[34] !== ContextMenuContentComponent || $[35] !== columnName || $[36] !== columnNamespace || $[37] !== isContextMenuOpen || $[38] !== width) {
    t11 = ContextMenuContentComponent && isContextMenuOpen ? /*#__PURE__*/_jsx(ContextMenuMinWidthContainer, {
      $minWidth: Math.min(width, 500),
      children: /*#__PURE__*/_jsx(StopPropogationContainer, {
        children: /*#__PURE__*/_jsx(ContextMenuContentComponent, {
          columnName: columnName,
          columnNamespace: columnNamespace,
          row: null
        })
      })
    }) : null;
    $[34] = ContextMenuContentComponent;
    $[35] = columnName;
    $[36] = columnNamespace;
    $[37] = isContextMenuOpen;
    $[38] = width;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const contextMenuPopover = t11;
  const externalId = `header-${columnNamespace}-${columnName}`;
  const isContact = columnNamespace === CONTACT_TYPE_ID;
  let t12;
  if ($[40] !== columnName || $[41] !== isContact) {
    t12 = isContact && columnName === "name" ? {
      "data-coaching-tips": "header-name"
    } : {};
    $[40] = columnName;
    $[41] = isContact;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const coachingTipsSelectors = t12;
  let t13;
  if ($[43] !== columnName || $[44] !== isContact) {
    t13 = isContact ? {
      "data-onboarding": `crm-table-header-cell-${columnName}`
    } : {};
    $[43] = columnName;
    $[44] = isContact;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const onboardingSelectors = t13;
  let t14;
  if ($[46] !== columnName || $[47] !== columnNamespace || $[48] !== triggerContextMenu) {
    t14 = triggerContextMenu ? e => {
      if (shouldBypassContextMenu(e)) {
        return;
      }
      const handled = triggerContextMenu({
        row: null,
        columnNamespace,
        columnName
      });
      if (handled === true) {
        e.preventDefault();
        e.stopPropagation();
      }
    } : undefined;
    $[46] = columnName;
    $[47] = columnNamespace;
    $[48] = triggerContextMenu;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const handleContextMenu = t14;
  const t15 = `${width}px`;
  const t16 = !sortable;
  const t17 = !sortable;
  let t18;
  if ($[50] !== sortDirection) {
    t18 = getSortFromDirection(sortDirection);
    $[50] = sortDirection;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== Content || $[53] !== children || $[54] !== coachingTipsSelectors || $[55] !== columnIndex || $[56] !== externalId || $[57] !== handleContextMenu || $[58] !== handleSortChange || $[59] !== onboardingSelectors || $[60] !== sortDirection || $[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== t18 || $[65] !== width) {
    t19 = /*#__PURE__*/_jsx(SortTH, Object.assign({}, coachingTipsSelectors, onboardingSelectors, {
      "data-column-index": columnIndex,
      "data-test-width": width,
      inlineSize: t15,
      padding: "0",
      role: "columnheader",
      "data-table-external-id": externalId,
      "data-test-id": externalId,
      "data-test-sort-direction": sortDirection,
      Content: Content,
      FlexContainer: Fragment,
      disabled: t16,
      "aria-disabled": t17,
      sort: t18,
      onSortChange: handleSortChange,
      onContextMenu: handleContextMenu,
      children: children
    }));
    $[52] = Content;
    $[53] = children;
    $[54] = coachingTipsSelectors;
    $[55] = columnIndex;
    $[56] = externalId;
    $[57] = handleContextMenu;
    $[58] = handleSortChange;
    $[59] = onboardingSelectors;
    $[60] = sortDirection;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
    $[65] = width;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  const sortTH = t19;
  let t20;
  if ($[67] !== contextMenuPopover || $[68] !== onCloseContextMenu || $[69] !== sortTH) {
    t20 = contextMenuPopover ? /*#__PURE__*/_jsx(Popover, {
      arrowSize: "none",
      closeOnOutsideClick: true,
      content: contextMenuPopover,
      "data-test-id": "context-menu-popover",
      onOpenChange: onCloseContextMenu,
      open: true,
      placement: "bottom right",
      children: sortTH
    }) : sortTH;
    $[67] = contextMenuPopover;
    $[68] = onCloseContextMenu;
    $[69] = sortTH;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  return t20;
};
export default ResizableSortTH;
function _temp(e) {
  e.stopPropagation();
}